import React, { useEffect, useState } from 'react';
import { AlertTriangle, Wrench, ExternalLink, Loader2, Power } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../App';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';

/**
 * Site Maintenance Mode card.
 *
 * Lives in Admin → Utilities. Toggling ON causes non-admin visitors to be
 * redirected to the standalone /maintenance page. Admins are never blocked
 * — /admin routes remain reachable so this toggle can always be flipped
 * back off from the browser.
 */
const DEFAULT_MESSAGE = "Wrapped In Music is currently under maintenance. We'll be back shortly.";

const AdminMaintenanceModeCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/site/maintenance');
      if (res.ok) {
        const data = await res.json();
        setEnabled(!!data.enabled);
        setMessage(data.message || DEFAULT_MESSAGE);
        setUpdatedAt(data.updated_at || null);
        setUpdatedBy(data.updated_by || null);
      }
    } catch (err) {
      toast.error(`Could not load maintenance status: ${err?.message || 'unknown'}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (nextEnabled, nextMessage) => {
    setSaving(true);
    try {
      const { data } = await apiFetch('/admin/settings/maintenance', {
        method: 'PUT',
        body: JSON.stringify({ enabled: nextEnabled, message: nextMessage }),
      });
      setEnabled(!!data.enabled);
      setMessage(data.message || DEFAULT_MESSAGE);
      setUpdatedAt(data.updated_at || null);
      setUpdatedBy(data.updated_by || null);
      toast.success(
        nextEnabled
          ? 'Maintenance mode ENABLED — non-admin visitors will see the maintenance page.'
          : 'Maintenance mode disabled — site is live.',
      );
    } catch (err) {
      toast.error(`Could not update: ${err?.response?.data?.detail || err?.message || 'unknown'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (checked) => {
    if (checked) {
      const ok = window.confirm(
        'Turn ON maintenance mode?\n\n' +
        'All non-admin visitors (including anyone hitting the home page, ' +
        'trying to log in, or navigating to any protected page) will be ' +
        'redirected to /maintenance until you turn this off.\n\n' +
        'Admins can still access /admin. Proceed?'
      );
      if (!ok) return;
    }
    await save(checked, message);
  };

  const handleSaveMessage = async () => {
    const trimmed = (message || '').trim() || DEFAULT_MESSAGE;
    setMessage(trimmed);
    await save(enabled, trimmed);
  };

  return (
    <div
      className={`glass-card p-6 border-2 transition-colors ${enabled ? 'border-amber-500/60 bg-amber-500/5' : 'border-transparent'}`}
      data-testid="admin-maintenance-card"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Wrench className={`h-5 w-5 ${enabled ? 'text-amber-300' : 'text-purple-400'}`} />
          <h3 className="text-lg font-bold text-white">Site Maintenance Mode</h3>
          {enabled && (
            <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-xs font-medium text-amber-200">
              <Power className="h-3 w-3" /> LIVE
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
          ) : (
            <>
              <span className="text-xs text-zinc-400">{enabled ? 'ON' : 'OFF'}</span>
              <Switch
                checked={enabled}
                onCheckedChange={handleToggle}
                disabled={saving}
                data-testid="maintenance-toggle"
              />
            </>
          )}
        </div>
      </div>

      <p className="text-sm text-zinc-400 mb-4">
        When enabled, all non-admin visitors are redirected to <code className="text-zinc-300">/maintenance</code>.
        Admins bypass automatically — <code className="text-zinc-300">/admin</code> stays reachable so you can turn this back off.
      </p>

      <div className="mb-3">
        <label className="text-xs uppercase tracking-wide text-zinc-400 mb-1 block">
          Message shown to visitors
        </label>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          className="bg-black/30 border-white/10 text-sm"
          placeholder={DEFAULT_MESSAGE}
          data-testid="maintenance-message-input"
          disabled={saving}
        />
      </div>

      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleSaveMessage}
            disabled={saving}
            data-testid="maintenance-save-message-btn"
            className="text-xs"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            Save message
          </Button>
          <a
            href="/maintenance?preview=1"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-purple-300 hover:text-purple-200 underline underline-offset-4"
            data-testid="maintenance-preview-link"
          >
            <ExternalLink className="h-3 w-3" /> Preview page
          </a>
        </div>
        <div className="text-xs text-zinc-500">
          {updatedAt && (
            <span>
              Last updated {new Date(updatedAt).toLocaleString()}
              {updatedBy && <> by <code className="text-zinc-400">{updatedBy}</code></>}
            </span>
          )}
        </div>
      </div>

      {enabled && (
        <div className="mt-4 flex items-start gap-2 rounded-md bg-amber-500/10 border border-amber-500/30 p-3">
          <AlertTriangle className="h-4 w-4 text-amber-300 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-100 leading-relaxed">
            <strong>Maintenance mode is ON.</strong> If you log out or open the site in a different browser without an admin session, you&apos;ll see the maintenance page. Bookmark <code className="text-amber-200">/admin</code> so you can always get back in.
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminMaintenanceModeCard;
